import { LockOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Carousel, Col, Form, Input, message, Row, Space, Typography, } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import image1 from "../assets/image1.svg";
import image2 from "../assets/image2.svg";
import logo from "../assets/logo.png";
import CustomButton from "../common/CustomButton";
import { useAuth } from "../config/AuthProvider";
import AuthService from "../services/Auth/AuthService";

const { Title, Text } = Typography;

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState("login");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (location.state?.message) {
      message.success(location.state.message);
    }
  }, [location.state]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let response;
      if (view === "login") {
        response = await login(values);
      } else if (view === "register") {
        response = await AuthService.registerUser(values);
      } else {
        setLoading(false);
        return;
      }
      if (response) {
        if (response?.status === 200) {
          if (view === "login") {
            message.success("Login Successful");
            if (response.data?.isPasswordResetRequired) {
              navigate("/reset-password", {
                state: { email: values.email },
                replace: true,
              });
            } else {
              navigate("/", { replace: true });
            }
          } else {
            message.success("OTP Sent, Please check your Email");
          }
        }
      } else {
        view === "login" && message.error("Invalid Credentials");
      }
    } catch (error) {
      message.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (view) {
      case "login":
        return (
          <Form
            className="mt-6"
            name="login"
            onFinish={onFinish}
            layout="vertical"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please input your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email Address" />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please input your password" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
              />
            </Form.Item>
            <CustomButton
              type="primary"
              htmlType="submit"
              className="bg-primary w-full h-11 hover:!bg-teal-500"
              loading={loading}
            >
              Login
            </CustomButton>
            <div className="flex justify-between">
              <div className="text-center mt-3">
                <Text>Forgot Password?</Text>
                <Text
                  className="text-primary ml-1 hover:text-teal-500 cursor-pointer"
                  onClick={() => navigate("/forgot-password")}
                >
                  Reset
                </Text>
              </div>
              <div className="text-center mt-3">
                <Text>New Here?</Text>
                <Text
                  className=" text-primary ml-1 hover:text-teal-500 cursor-pointer"
                  onClick={() => setView("register")}
                >
                  Sign Up
                </Text>
              </div>
            </div>
          </Form>
        );
      case "register":
        return (
          <Form
            key="register"
            name="register"
            onFinish={onFinish}
            layout="vertical"
          >
            <Space.Compact className="w-full">
              <Form.Item
                name="firstName"
                rules={[
                  { required: true, message: "Please enter your first name" },
                ]}
                className="w-1/2"
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="First Name"
                  className="rounded-md"
                />
              </Form.Item>
              <Form.Item
                name="lastName"
                rules={[
                  { required: true, message: "Please enter your last name" },
                ]}
                className="w-1/2"
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Last Name"
                  className="rounded-md"
                />
              </Form.Item>
            </Space.Compact>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email Address"
                className="rounded-md"
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                className="rounded-md"
              />
            </Form.Item>
            <CustomButton
              type="primary"
              htmlType="submit"
              className="bg-primary w-full h-11 hover:!bg-teal-500"
              loading={loading}
            >
              Sign Up
            </CustomButton>
            <div className="text-center mt-3">
              <Text>Already Registered?</Text>
              <Text
                className="text-primary ml-1 hover:text-teal-500 cursor-pointer"
                onClick={() => setView("login")}
              >
                Login
              </Text>
            </div>
          </Form>
        );
      default:
        return null;
    }
  };

  return (
    <Row className="h-screen overflow-hidden">
      <Col
        xs={24}
        md={12}
        className="flex items-center justify-center h-full overflow-hidden"
      >
        <div className="relative w-full max-w-lg p-8 bg-white shadow-2xl rounded-xl transition-transform duration-700">
          <div className="text-center mb-6">
            <img
              src={logo}
              alt="Conti Logo"
              className="demo-logo-vertical text-center w-3/5"
            />
            <Title level={4} className="text-gray-600">
              Welcome to CONTi
            </Title>
            <Text className="text-tertiary">
              {view === "login"
                ? "Login to your account"
                : view === "register"
                  ? "Create an account to get started"
                  : "Reset your password"}
            </Text>
          </div>
          {renderForm()}
        </div>
      </Col>

      <Col xs={0} md={12} className="hidden md:flex h-screen">
        <div className="relative w-full">
          <Carousel
            dotPosition="top"
            autoplay
            autoplaySpeed={8000}
            speed={2000}
          >
            <img src={image1} alt="image1" />
            <img src={image2} alt="image2" />
          </Carousel>
        </div>
      </Col>
    </Row>
  );
};

export default Login;
